import React, { useCallback, useState } from 'react'
import { useForm, useWatch, useFormState } from 'react-hook-form'
import type { SubmitHandler } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'
import { useSearchParam } from 'react-use'

import { TtIcon, TtIconButton } from '@23/ui-react'

import Button from '../../../../../components/ButtonComponent/ButtonComponent'
import { useWebinarColors } from '../../../../../../Designs/blocs/useWebinarColors'
import {
  JoinToSpeakIDSearchParamName,
  JoinToSpeakPresenterTokenSearchParamName,
  JoinToSpeakQuestionSearchParamName,
  useJoinToSpeakAsAudience,
  useOnDenied,
  clearJoinToSpeakSearchParams,
} from '../../../../../join-to-speak'
import { replaceSearchParams } from '../../../../../../global/utils/searchParams'
import { WebinarPanelComponent } from '../../../../WebinarSidebarFeature/components'
import { useOpenDeviceSelector } from '../../../../../../Devices/components/DeviceSelector/DeviceSelector'
import { useAudienceTracks } from '../../../../../join-to-speak/audienceTracks'
import { changeSelectedDevices } from '../../../../../../Devices/useDevices'

import { JoinToSpeakPreview } from './Preview/JoinToSpeakPreview'

import './JoinToSpeakAccess.scss'

type JoinToSpeakAccessForm = {
  question: string
}

type JoinToSpeakAccessProps = {
  sessionId: string
  onClose: () => void
}

const MaxNumberOfCharacters = 240

const JoinToSpeakAccess = ({ sessionId, onClose }: JoinToSpeakAccessProps) => {
  const FORM_NAME = 'join-to-speak-access'
  const openDeviceSelector = useOpenDeviceSelector()

  const joinToSpeakID = useSearchParam(JoinToSpeakIDSearchParamName)
  const joinToSpeakPresenterToken = useSearchParam(
    JoinToSpeakPresenterTokenSearchParamName
  )
  const joinToSpeakQuestion = useSearchParam(JoinToSpeakQuestionSearchParamName)

  const [isRequestingAccess, setIsRequestingAccess] = useState(
    ![joinToSpeakID, joinToSpeakPresenterToken, joinToSpeakQuestion].includes(
      null
    )
  )

  const { audienceVideoTrack, audienceAudioTrack, removeAllAudienceTracks } =
    useAudienceTracks()

  const form = useForm<JoinToSpeakAccessForm>({
    defaultValues: {
      question: joinToSpeakQuestion !== null ? joinToSpeakQuestion : '',
    },
    mode: 'onChange',
  })
  const formState = useFormState({ name: 'question', control: form.control })
  const questionLength = useWatch({
    name: 'question',
    control: form.control,
  }).length
  const isOverCharacterLimit = formState.errors.question?.type === 'maxLength'
  const { requestToJoin, cancelRequestToJoin } = useJoinToSpeakAsAudience()
  const webinarColors = useWebinarColors()

  useOnDenied((message) => {
    if (message.data.joinToSpeakID === joinToSpeakID) {
      setIsRequestingAccess(false)
    }
  })

  const handleValidSubmit: SubmitHandler<JoinToSpeakAccessForm> = useCallback(
    async ({ question }) => {
      if (isOverCharacterLimit) {
        return
      }

      setIsRequestingAccess(true)

      try {
        const response = await requestToJoin(question)

        const url = new URL(location.href)
        url.searchParams.set(
          JoinToSpeakIDSearchParamName,
          response.joinToSpeakID
        )
        url.searchParams.set(
          JoinToSpeakPresenterTokenSearchParamName,
          response.joinToSpeakPresenterToken
        )
        url.searchParams.set(JoinToSpeakQuestionSearchParamName, question)
        replaceSearchParams(url)
      } catch (err) {
        setIsRequestingAccess(false)
        throw err
      }
    },
    [requestToJoin, isOverCharacterLimit]
  )

  const handleCancelClick = useCallback(async () => {
    if (isRequestingAccess) {
      await cancelRequestToJoin()
      setIsRequestingAccess(false)
    }

    clearJoinToSpeakSearchParams()
  }, [isRequestingAccess, cancelRequestToJoin])

  const handleCloseClick = useCallback(async () => {
    await handleCancelClick()
    removeAllAudienceTracks()
    onClose()
  }, [handleCancelClick, onClose, removeAllAudienceTracks])

  const handleOpenDeviceSelector = useCallback(async () => {
    const devicesSelected = await openDeviceSelector()
    changeSelectedDevices(devicesSelected)
  }, [openDeviceSelector])

  return (
    <WebinarPanelComponent className="join-to-speak-access">
      {isRequestingAccess && (
        <div className="status">
          <div className="icon-wrapper">
            <TtIcon name="check" />
          </div>
          <div>
            <FormattedMessage defaultMessage="Hang in there - the host will let you in, if there is time." />
          </div>
        </div>
      )}
      {!isRequestingAccess && (
        <div
          className="textarea-wrapper"
          data-number-of-characters={questionLength}
          data-max-number-of-characters={MaxNumberOfCharacters}
          data-is-over-character-limit={isOverCharacterLimit}
          data-is-requesting-access={isRequestingAccess}
        >
          <textarea
            form={FORM_NAME}
            placeholder={intl.formatMessage({
              defaultMessage: 'What do you want to talk about (optional)',
            })}
            {...form.register('question', {
              maxLength: MaxNumberOfCharacters,
              disabled: isRequestingAccess,
            })}
            maxLength={MaxNumberOfCharacters}
            onInput={(event) => {
              // oxlint-disable-next-line no-param-reassign
              event.currentTarget.style.height = ''
              // oxlint-disable-next-line no-param-reassign
              event.currentTarget.style.height = `${
                event.currentTarget.scrollHeight + 3
              }px`
            }}
          />
        </div>
      )}
      <JoinToSpeakPreview
        sessionId={sessionId}
        isRequestingAccess={isRequestingAccess}
        audienceVideoTrack={audienceVideoTrack}
        audienceAudioTrack={audienceAudioTrack}
      />
      <p className="description">
        {!isRequestingAccess
          ? intl.formatMessage({
              defaultMessage:
                "Looking sharp? Make sure your microphone is running then hit 'Ask to go on camera' when you're all set.",
            })
          : intl.formatMessage({
              defaultMessage: 'Waiting to go on camera',
            })}
      </p>
      <form
        id={FORM_NAME}
        onSubmit={form.handleSubmit(handleValidSubmit)}
        data-is-requesting-access={isRequestingAccess}
      >
        {!isRequestingAccess && (
          <TtIconButton
            className="devices-change"
            name="devices-change"
            label={intl.formatMessage({
              defaultMessage: 'Device change',
            })}
            color="themed"
            style={{
              fontSize: 22,
              '--hover-color': webinarColors.accent.color,
            }}
            onClick={handleOpenDeviceSelector}
          />
        )}
        <Button
          type="button"
          className="button"
          color="themed"
          style={{
            color: webinarColors.tabs.contrast.color,
            backgroundColor: webinarColors.tabs.contrast.transparent('5%'),
          }}
          onClick={handleCloseClick}
          round
        >
          <FormattedMessage defaultMessage="Cancel" />
        </Button>
        {!isRequestingAccess && (
          <Button
            type="button"
            className="button button-accept"
            color="themed"
            style={{
              backgroundColor: webinarColors.accent.color,
              color: webinarColors.accent.contrast.color,
            }}
            disabled={isOverCharacterLimit}
            onClick={form.handleSubmit(handleValidSubmit)}
            round
          >
            <FormattedMessage defaultMessage="Ask to go on camera" />
          </Button>
        )}
      </form>
    </WebinarPanelComponent>
  )
}

export { JoinToSpeakAccess }
